import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Button, LockIcon } from '@farewill/ui'
import { GTR } from '@farewill/ui/tokens'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import { ProbateQuoteHistory } from 'lib/models/probate-quote-history'
import { VAT_RATE } from 'utils/enums'
import { formatCurrency, hasDecimals } from 'lib/formatting/currency'
import { formatDate } from 'lib/formatting/dates'
import { noop } from 'lib/functions/noop'
import { showModal } from 'state/actions'
import { SelectInput } from 'components/form'
import EditPriceModal from './edit-price-modal'
import { LeadVoucher } from 'lib/models/lead'
import { PriceRowAttributes, PriceTable } from './price-table'
import { FinalPriceBreakdown } from './final-price-breakdown'

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${GTR.M};
  margin-bottom: ${GTR.M};
`

const StyledRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${GTR.S};
`

const StyledSelectWrapper = styled.div`
  flex: 1;
  max-width: 320px;
`

const DIGNITY_VOUCHERS_LABELS = [
  { label: 'Farewill Membership', value: 'dgntymem' },
  { label: '10% Dignity Family & Friends', value: 'dgntyfnf' },
]

type PricingSummaryProps = {
  availableVouchers?: LeadVoucher[]
  quoteHistory: ProbateQuoteHistory[]
  selectedQuoteIndex: number
  onSelectedQuoteIndexChange: (quoteIndex: number) => void
}

export const PricingSummary = ({
  availableVouchers,
  quoteHistory,
  selectedQuoteIndex,
  onSelectedQuoteIndexChange,
}: PricingSummaryProps) => {
  const dispatch = useDispatch()
  const latestQuoteIndex = quoteHistory.length - 1
  const latestQuote = quoteHistory[latestQuoteIndex]
  const previousLatestQuoteIdRef = useRef(latestQuote?.id)

  useEffect(() => {
    if (!latestQuote) return

    if (latestQuote.id !== previousLatestQuoteIdRef.current) {
      previousLatestQuoteIdRef.current = latestQuote.id
      onSelectedQuoteIndexChange(latestQuoteIndex)
    }
  }, [latestQuote, latestQuoteIndex, onSelectedQuoteIndexChange])

  const selectedQuote = quoteHistory[selectedQuoteIndex]
  const isViewingOlderQuote = selectedQuote !== latestQuote

  const latestServiceFeeInPence = latestQuote.attributes.serviceFeeExclVat

  const totalEstimatedDisbursements = (
    selectedQuote.attributes.disbursementBreakdown || []
  ).reduce((acc, { price, quantity }) => acc + price * (quantity || 0), 0)

  const formatPrice = (valueInPence: number) =>
    formatCurrency({
      value: valueInPence,
      valueInPence: true,
      showPence: hasDecimals(valueInPence / 100),
    })

  const quoteOptions = quoteHistory.map((_, index) => ({
    label: `Quote ${index + 1}`,
    value: index,
  }))

  const voucherDiscountAmount =
    selectedQuote.attributes.voucherDiscountAmount || 0

  const voucherLabel =
    DIGNITY_VOUCHERS_LABELS.find(
      (voucher) => voucher.value === selectedQuote.attributes.voucherCode
    )?.label || selectedQuote.attributes.voucherCode

  const discountLabel = `Discount ${voucherLabel ? `(${voucherLabel})` : ''}`
  const serviceFeeExclVat = selectedQuote.attributes.serviceFeeExclVat

  const quoteInformation = [
    {
      name: 'Service fee (excl. VAT)',
      price: formatPrice(serviceFeeExclVat),
    },
    {
      name: '20% VAT',
      price: formatPrice(Math.round(serviceFeeExclVat * VAT_RATE)),
    },
    {
      name: discountLabel,
      price: formatPrice(voucherDiscountAmount ? -voucherDiscountAmount : 0),
    },
    {
      name: 'Estimated disbursements',
      price: formatPrice(totalEstimatedDisbursements),
    },
  ] as PriceRowAttributes[]
  return (
    <>
      <StyledContainer>
        <StyledRow>
          <StyledSelectWrapper>
            <Formik
              initialValues={{ quoteHistoryIndex: selectedQuoteIndex }}
              onSubmit={noop}
              enableReinitialize
            >
              <SelectInput
                name="quoteHistoryIndex"
                label="Quote history"
                options={quoteOptions}
                handleSave={({ value }) => {
                  if (typeof value === 'number') {
                    onSelectedQuoteIndexChange(value)
                  }
                }}
              />
            </Formik>
          </StyledSelectWrapper>
          {!isViewingOlderQuote && (
            <Button.Primary
              onClick={() => {
                dispatch(
                  showModal({
                    component: EditPriceModal,
                    headingText: 'Edit price',
                    maxWidth: 800,
                    config: {
                      currentPriceInPence: latestServiceFeeInPence,
                      availableVouchers,
                      currentVoucher: latestQuote.attributes.voucherCode,
                    },
                  })
                )
              }}
            >
              Update quote
            </Button.Primary>
          )}
        </StyledRow>
        <span>
          Created on:{' '}
          <strong>{formatDate(selectedQuote.attributes.createdAt)}</strong>
        </span>
        {selectedQuote.attributes.reasonForChange && (
          <span>
            Reason for change:{' '}
            <strong>{selectedQuote.attributes.reasonForChange}</strong>
          </span>
        )}
        {isViewingOlderQuote && (
          <span>
            <LockIcon /> <strong>This quote is outdated and view-only</strong>
          </span>
        )}
        <PriceTable
          rows={quoteInformation}
          title="Quote"
          isDisabled={isViewingOlderQuote}
        />
      </StyledContainer>
      <FinalPriceBreakdown
        quotedPrice={serviceFeeExclVat}
        discount={voucherDiscountAmount}
        disbursementBreakdown={selectedQuote.attributes.disbursementBreakdown}
        isDisabled={isViewingOlderQuote}
        version={selectedQuote.attributes.quoteVersion}
        isOutdatedVersion={selectedQuote.attributes.isOutdatedVersion}
        latestQuoteVersion={selectedQuote.attributes.latestQuoteVersion}
      />
    </>
  )
}
